<script setup lang="ts">
/**
 * Навигация панели.
 *
 * Все имена иконок сверены с установленным `@lucide/vue@1.28` — реестр
 * shadcn-vue 2.8 переехал на этот пакет, и часть имён в нём отличается от
 * старых доков `lucide-vue-next` (канонический пример: `FileQuestion`
 * не существует, есть `FileQuestionMark`).
 */
import type { Component } from 'vue'
import { ClipboardList, KeyRound, MonitorSmartphone, ScanLine, UserRound, Users } from '@lucide/vue'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'

interface NavItem {
  /** Имя маршрута — ссылки строятся по имени, а не по пути. */
  name: string
  label: string
  icon: Component
  /**
   * Имена маршрутов, при которых пункт подсвечен. Отдельно от `name`, потому
   * что `/users/new` и `/users/:id` — **не** дети `users` (они соседи в дереве
   * маршрутов), и `route.matched` их с «Пользователями» не свяжет.
   */
  matches: readonly string[]
}

interface NavGroup {
  label: string
  items: readonly NavItem[]
}

const NAV: readonly NavGroup[] = [
  {
    label: 'Управление',
    items: [
      {
        name: 'users',
        label: 'Пользователи',
        icon: Users,
        matches: ['users', 'user-create', 'user-edit'],
      },
      /**
       * `ClipboardList`, а не `Users`/`UsersRound`: в свёрнутой полоске иконок
       * остаётся только глиф, и два почти одинаковых силуэта людей означали бы
       * два неразличимых пункта. «Список» же читается как список участников и
       * не спорит с «Пользователями».
       */
      {
        name: 'participants',
        label: 'Участники',
        icon: ClipboardList,
        matches: ['participants', 'participant-create', 'participant-edit', 'participants-import'],
      },
    ],
  },
  {
    label: 'Аккаунт',
    items: [
      { name: 'profile', label: 'Мой профиль', icon: UserRound, matches: ['profile'] },
      { name: 'change-password', label: 'Смена пароля', icon: KeyRound, matches: ['change-password'] },
      { name: 'sessions', label: 'Активные сессии', icon: MonitorSmartphone, matches: ['sessions'] },
    ],
  },
]

const route = useRoute()

/**
 * Активный пункт считается один раз на навигацию, а не по разу на пункт
 * в шаблоне.
 */
const activeName = computed(() => (typeof route.name === 'string' ? route.name : ''))

function isActive(item: NavItem): boolean {
  return item.matches.includes(activeName.value)
}
</script>

<template>
  <!--
    `collapsible="icon"` — сворачивание в полоску иконок (⌘/Ctrl+B или кнопка
    в шапке), а не полное исчезновение: на узком ноутбуке место нужно, но
    терять навигацию целиком незачем. На мобильном `Sidebar` сам подменяется
    на `Sheet`.
  -->
  <Sidebar collapsible="icon">
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton as-child size="lg">
            <RouterLink :to="{ name: 'users' }">
              <div class="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <ScanLine class="size-4" />
              </div>
              <div class="grid flex-1 text-left text-sm leading-tight">
                <span class="truncate font-semibold">Сканер</span>
                <span class="truncate text-xs text-muted-foreground">Админ-панель</span>
              </div>
            </RouterLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>

    <SidebarContent>
      <SidebarGroup v-for="group in NAV" :key="group.label">
        <SidebarGroupLabel>{{ group.label }}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem v-for="item in group.items" :key="item.name">
              <!--
                `as-child` + `RouterLink`: кнопка отдаёт свои классы настоящему
                `<a href>`. Обычный `@click="router.push(...)"` лишил бы ссылку
                middle-click, «открыть в новой вкладке» и предпросмотра адреса
                в статусной строке.
                `tooltip` показывается только в свёрнутом состоянии — это делает
                сам `SidebarMenuButton`.
              -->
              <SidebarMenuButton as-child :is-active="isActive(item)" :tooltip="item.label">
                <RouterLink :to="{ name: item.name }">
                  <component :is="item.icon" />
                  <span>{{ item.label }}</span>
                </RouterLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>

    <SidebarFooter>
      <!-- В свёрнутом виде подпись прячется, чтобы не рвать полоску иконок. -->
      <div class="px-2 py-1 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
        Сканер · панель администратора
      </div>
    </SidebarFooter>
  </Sidebar>
</template>
